<template>
  <article class="number-lottery-play-slip-item -mt-200 pb-300">
    <ul class="flex flex-col items-center px-100 pb-100">
      <li
        v-for="({ name: fieldName, selectableMax, selectableNumbers }, fieldIndex) in fieldConfig"
        :key="fieldName"
        ref="fields"
        class="play w-full"
      >
        <header class="flex items-center justify-between my-200">
          <NumberLotteryBaseRemainingNumbers
            v-bind="{ fieldName }"
            :remaining="selectableMax - play.fields[fieldIndex].selected.length"
          />
          <NumberLotteryBaseExtraHit
            v-if="extraHit && fieldName === 'EXTRA'"
            v-bind="{ isExtraHitEnabled, lotteryColor, lotteryId }"
            @update:value="toggleExtraHit"
          />
        </header>

        <LcLotteryNumberSelector
          v-bind="{ disabled, selectableMax, selectableNumbers }"
          :all-numbers-toggled="isExtraHitEnabled && fieldName === 'EXTRA'"
          :fixed-columns="7"
          :label="$t('numberlottery.playslip.selector.legend')"
          :style="[
            {
              '--lottery-numbers-extra-bg': lotteryColor,
            },
          ]"
          :value="play.fields[fieldIndex].selected"
          @complete="scrollToNextIncompleteField"
          @update:value="(numbers) => setSelectedNumbersForField(play.id, fieldIndex, numbers)"
        />
      </li>
    </ul>
  </article>
</template>

<script lang="ts" setup>
import type { PlaySlipPlay } from '#product/app/composables/playSlip'

type NumberLotteryBasePlaySlipItemEmits = {
  (e: 'play-state-changed', isDirty: boolean, playId: string): void
}

type NumberLotteryBasePlaySlipItemProps = {
  disabled?: boolean
  play: PlaySlipPlay
}

const { play } = defineProps<NumberLotteryBasePlaySlipItemProps>()
const emit = defineEmits<NumberLotteryBasePlaySlipItemEmits>()

const fieldRefs = useTemplateRef<HTMLElement[]>('fields')

const {
  extraHit,
  fieldConfig,
  isExtraHitEnabled: isExtraHitEnabledFn,
  licensedTerritory,
  lotteryId,
  perPlayAddOn,
  setSelectedNumbersForField,
  setSelectedPerPlayAddOn,
} = usePlaySlip()
const { scrollTop } = useScrollUtils()

const isExtraHitEnabled = computed(() => isExtraHitEnabledFn(play.id))
const isPlayComplete = computed(() => play.isComplete)
const lotteryCssVarConstructor = computed(() =>
  getLotteryCssVarConstructor(licensedTerritory, lotteryId),
)
const lotteryColor = computed(() => {
  const { createCssVar } = lotteryCssVarConstructor.value
  return createCssVar('extra-ball-bg')
})

const scrollToNextIncompleteField = () => {
  const nextIncompleteFieldIndex = play.fields.findIndex(
    ({ selected }, index) => selected.length < fieldConfig.value[index].selectableMax,
  )
  if (nextIncompleteFieldIndex < 0) return

  const nextFieldRef = fieldRefs.value?.[nextIncompleteFieldIndex]
  if (!nextFieldRef) return

  scrollTop(nextFieldRef.offsetTop)
}

const toggleExtraHit = (value: boolean) => {
  setSelectedPerPlayAddOn(play.id, {
    name: perPlayAddOn.value?.name ?? '',
    value: value ? 'true' : '',
  })
}

watch(isPlayComplete, () => {
  emit('play-state-changed', isPlayComplete.value, play.id)
})
</script>

<style lang="scss" scoped>
.number-lottery-play-slip-item {
  .play {
    max-width: 22.438rem;
  }
}
</style>
